import {
  FormControl,
  RadioGroup,
  Stack,
  Radio,
  Text,
  FormLabel,
  Input,
  VStack,
  Button,
} from '@chakra-ui/react'
import { BigNumberish, ethers } from 'ethers'
import { useContext, useRef, useState } from 'react'
import {
  useAccount,
  useContractEvent,
  useContractReads,
  useContractWrite,
  usePrepareContractWrite,
  useWaitForTransaction,
} from 'wagmi'
import { SplitPayContext } from '../../context/SplitPayContext'
import SplitPayServerABI from '../../assets/constants/abi/SplitPayServer.json'
import NetworkMapping from '../../assets/constants/network-mapping.json'
import { useDebounce } from '../../hooks'

const StartPage = () => {
  const [contractState, setContractState] = useState('ExistingContract')
  const { address } = useAccount()
  const { setSplitState, splitState } = useContext(SplitPayContext)
  const [recipientsText, setRecipientsText] = useState<string>('')
  const [sharesText, setSharesText] = useState<string>('')
  const [splitId, setSplitId] = useState<number>(0)
  const debouncedRecipients = useDebounce(recipientsText, 500)
  const debouncedShares = useDebounce(sharesText, 500)
  const newSplitId = useRef('')

  function prepareRecipientsAndShares() {
    const addrsArray: string[] = debouncedRecipients.split(',').map((addr) => addr.trim())
    const shrsArray: string[] = debouncedShares.split(',').map((shr) => shr.trim())
    return [addrsArray, shrsArray]
  }

  const { config, isError: isPrepareError } = usePrepareContractWrite({
    address: NetworkMapping['80001']['SplitPayServer'] as `0x${string}`,
    abi: SplitPayServerABI,
    functionName: 'createSplitPay',
    args: prepareRecipientsAndShares(),
  })

  const { data, write: createSplit } = useContractWrite(config)

  const { isLoading } = useWaitForTransaction({
    hash: data?.hash,
  })

  const { refetch, isError } = useContractReads({
    contracts: [
      {
        address: NetworkMapping['80001']['SplitPayServer'] as `0x${string}`,
        abi: SplitPayServerABI,
        functionName: 'getPayeeDetails',
        args: [
          contractState === 'NewContract' && newSplitId ? Number(newSplitId.current) : splitId,
        ],
      },
      {
        address: NetworkMapping['80001']['SplitPayServer'] as `0x${string}`,
        abi: SplitPayServerABI,
        functionName: 'SplitIdToContract',
        args: [
          contractState === 'NewContract' && newSplitId ? Number(newSplitId.current) : splitId,
        ],
      },
    ],
    enabled: false,
    onSuccess(data: any) {
      if (data[0] && data[1]) {
        const shareList: number[] = data[0][1].map((shr: any) =>
          Number(ethers.utils.formatUnits(shr, 0))
        )
        const totalShrs = shareList.reduce((sum: number, cur: number) => {
          return sum + cur
        }, 0)

        const amountReleaseList: number[] = data[0][2].map((amt: any) =>
          Number(ethers.utils.formatUnits(amt, 18))
        )
        const totalAmountReleased = amountReleaseList.reduce((sum: number, cur: number) => {
          return sum + cur
        }, 0)
        setSplitState({
          ...splitState,
          address: address,
          recipients: data[0][0],
          shares: shareList,
          amountReleased: amountReleaseList,
          state: 1,
          splitId:
            contractState === 'NewContract' && newSplitId ? Number(newSplitId.current) : splitId,
          contractAddress: data[1],
          totalShares: totalShrs,
          totalReleased: totalAmountReleased,
        })
      }
    },
  })

  useContractEvent({
    address: NetworkMapping['80001']['SplitPayServer'] as `0x${string}`,
    abi: SplitPayServerABI,
    eventName: 'SplitPayCreated',
    listener(logs) {
      newSplitId.current = ethers.utils.formatUnits(logs as BigNumberish, 0)
      refetch?.()
    },
  })

  function takeAction() {
    if (contractState === 'NewContract') {
      createSplit?.()
    } else if (contractState === 'ExistingContract') {
      refetch?.()
    }
  }

  return (
    <VStack minW={{ base: 'md', md: 'lg', lg: 'lg' }} spacing={'15px'}>
      <FormControl>
        <RadioGroup onChange={setContractState} value={contractState}>
          <Stack direction="column">
            <Radio size="md" colorScheme="teal" value="ExistingContract" isDisabled={isLoading}>
              <Text fontSize={'xl'}>Existing Split</Text>
            </Radio>
            <Radio size="md" colorScheme="teal" value="NewContract" isDisabled={isLoading}>
              <Text fontSize={'xl'}>New Split</Text>
            </Radio>
          </Stack>
        </RadioGroup>
      </FormControl>
      {contractState === 'ExistingContract' && (
        <FormControl>
          <FormLabel color={'teal'} fontWeight="bold">
            Split Id
          </FormLabel>
          <Input
            placeholder="Enter Split Id"
            _placeholder={{ color: 'gray.500' }}
            type="number"
            onChange={(e) => setSplitId(Number(e.target.value))}
            value={splitId}
            isDisabled={isLoading}
            maxW={'md'}
          />
        </FormControl>
      )}
      {contractState === 'NewContract' && (
        <FormControl id="recipient">
          <FormLabel>{`Recipient List`}</FormLabel>
          <Input
            placeholder="0x1234...5678, 0x1234...5678,..."
            _placeholder={{ color: 'gray.500' }}
            type="text"
            onChange={(e) => setRecipientsText(e.target.value)}
            value={recipientsText}
            isDisabled={isLoading}
            maxW={'md'}
          />
        </FormControl>
      )}
      {contractState === 'NewContract' && (
        <FormControl id="shares">
          <FormLabel>{`Share Distribution`}</FormLabel>
          <Input
            placeholder="1,1,...."
            _placeholder={{ color: 'gray.500' }}
            type="text"
            onChange={(e) => setSharesText(e.target.value)}
            value={sharesText}
            isDisabled={isLoading}
            maxW={'md'}
          />
        </FormControl>
      )}
      <Button
        colorScheme="teal"
        variant="outline"
        isDisabled={contractState === 'NewContract' ? isPrepareError || isLoading : isError}
        onClick={(e) => {
          e.preventDefault()
          takeAction()
        }}
      >
        {contractState === 'ExistingContract' ? 'Fetch Contract' : 'Create Split Contract'}
      </Button>
    </VStack>
  )
}
export default StartPage
